import { useState } from "react";
import axios from "axios";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscribers`, { email: email.trim(), source: "homepage" });
      setDone(true);
      setEmail("");
      toast.success("You're on the list — we'll be in touch.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not subscribe. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="newsletter-section"
      className="py-20 sm:py-24"
      style={{ background: "var(--dl-ink)", color: "var(--dl-bg)" }}
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <p
            className="overline"
            style={{ color: "rgba(250,249,246,0.6)" }}
          >
            <span
              className="divider-rule mr-3"
              style={{ background: "rgba(250,249,246,0.6)" }}
            />
            Seasonal Availability Guide
          </p>
          <h2
            className="mt-5 font-serif text-3xl sm:text-4xl tracking-tight leading-[1.1]"
            style={{ fontWeight: 400 }}
          >
            Be first to know about
            <br />
            <span className="serif-italic">quiet weeks &amp; last-minute rates.</span>
          </h2>
          <p
            className="mt-5 max-w-md text-base leading-relaxed"
            style={{ color: "rgba(250,249,246,0.75)" }}
          >
            A short, occasional email from us — never marketing spam. Just a heads-up
            when we have a cottage or shepherd&apos;s hut open at short notice, plus
            our pick of the season&apos;s best walks and country pubs.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="newsletter-form"
          className="lg:col-span-6 flex flex-col gap-4"
        >
          <label className="overline" style={{ color: "rgba(250,249,246,0.6)" }}>
            Your email
          </label>
          <div
            className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-0 sm:border-b"
            style={{ borderColor: "rgba(250,249,246,0.4)" }}
          >
            <div className="flex items-center gap-3 flex-1 border-b sm:border-b-0 pb-3 sm:pb-0 sm:pr-3"
              style={{ borderColor: "rgba(250,249,246,0.4)" }}
            >
              <Mail size={18} style={{ color: "rgba(250,249,246,0.6)" }} />
              <input
                data-testid="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="flex-1 bg-transparent border-0 outline-none py-3 text-base"
                style={{
                  color: "var(--dl-bg)",
                  fontFamily: "Outfit, sans-serif",
                }}
              />
            </div>
            <button
              type="submit"
              data-testid="newsletter-submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-60 self-start sm:self-auto"
              style={{
                background: "var(--dl-bg)",
                color: "var(--dl-ink)",
                borderColor: "var(--dl-bg)",
              }}
            >
              {done ? (
                <>
                  Subscribed <Check size={14} />
                </>
              ) : submitting ? (
                "Sending..."
              ) : (
                "Get the Guide"
              )}
            </button>
          </div>
          <p
            className="text-xs"
            style={{ color: "rgba(250,249,246,0.5)" }}
          >
            One email at a time. Unsubscribe anytime — no questions asked.
          </p>
        </form>
      </div>
    </section>
  );
}
